import { Framework } from '@/framework/instance';
import { Layer } from '@/layers/layer';
import { type TextLayer } from '@/layers/text-layer';
import { type ILayerProps, type IShapeExtension } from '@/version-types';
import { type ClipboardPlugin } from '../clipboard';
import { AbilityDependency, ClipboardData } from './types';

export class TextEditDependency extends Framework implements AbilityDependency {
    private tryGetClipboard() {
        const clipboard = this.framework.getInstance<ClipboardPlugin>('ClipboardPlugin');
        if (!clipboard) {
            throw new Error('ClipboardPlugin is not found');
        }
        return clipboard;
    }

    getClipboardData(event: ClipboardEvent): ClipboardData {
        const { html, text, template } = this.tryGetClipboard().getClipboardData(event);
        const layer = !template || Array.isArray(template) ? null : template.layer;
        const extraInfo = !template || Array.isArray(template) ? null : template.extraInfo;

        return { html, text, layer, extraInfo };
    }

    setClipboardData(layer: ILayerProps, extra: Record<string, any>) {
        this.tryGetClipboard().setClipboardData({ template: { layer, extraInfo: extra } });
    }

    setPlainTextToClipboard(text: string) {
        this.tryGetClipboard().setClipboardData({ text });
    }

    private tryGetTextLayer() {
        const textLayer = this.framework.getInstance<TextLayer>('TextLayer');
        if (!textLayer) {
            throw new Error('TextLayer is not found');
        }
        return textLayer;
    }

    cancelShapeText(layerId: string) {
        return this.tryGetTextLayer().cancelShapeEffect(layerId, {
            update: false,
            removeMaterialID: false,
        });
    }

    async applyShapeText(layerId: string, params: IShapeExtension) {
        const shapeType = params.curveTextType;
        return this.tryGetTextLayer().applyShapeText(layerId, shapeType, { params });
    }

    deleteLayer(layer: ILayerProps) {
        this.framework.getInstance(Layer).delete(layer.id, layer);
    }
}
